import React, { useState, useContext, useEffect } from 'react';
import { StoreContext } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import './Profile.css';

// +++ FIREBASE + UI ICONS + API IMPORTS +++
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { FaMobileAlt, FaEnvelope, FaSpinner, FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../api/apiService';

const Profile = () => {
  const { user, updateProfile, changePassword, logout } = useContext(StoreContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    doorNumber: '',
    streetName: '',
    zipCode: '',
    city: '',
    state: '',
    country: '',
  });

  const [editMode, setEditMode] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  // Reuse existing oldPassword / newPassword / confirmNewPassword state declared earlier
  // Use `oldPassword` as "current password" in modal flows to avoid duplicate declarations.
  const [passwordChangeStep, setPasswordChangeStep] = useState(1); // 1: choose,2:send,3:verify,4:set
  const [passwordChangeMethod, setPasswordChangeMethod] = useState('phone'); // default phone-first
  const [passwordChangeOtp, setPasswordChangeOtp] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
  const [firebaseConfirmation, setFirebaseConfirmation] = useState(null);
  const [loadingFinal, setLoadingFinal] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // +++ TEMP STORE FOR FIREBASE TOKEN GENERATED AFTER PHONE OTP VERIFICATION +++
  const [tempFirebaseToken, setTempFirebaseToken] = useState(null);


  // OTP states / cooldowns
  const [phoneOtpState, setPhoneOtpState] = useState({ count: 0, loading: false, cooldown: 0, sent: false });
  /*
  // Email OTP state commented out (email flow disabled)
  const [emailOtpState, setEmailOtpState] = useState({ count: 0, loading: false, cooldown: 0, sent: false });
  */

  // --- NEW: Edit Profile verification modal state (Firebase phone OTP) ---
  const [showEditVerifyModal, setShowEditVerifyModal] = useState(false);
  const [editVerifyStep, setEditVerifyStep] = useState(1); // 1: sending, 2: verify
  const [editVerifyOtp, setEditVerifyOtp] = useState('');
  const [editVerifyError, setEditVerifyError] = useState('');
  const [editVerifyState, setEditVerifyState] = useState({ loading: false, cooldown: 0, sent: false, count: 0 });
  // We will reuse firebaseConfirmation and Recaptcha globals

  const MAX_RESEND_ATTEMPTS = 3;
  const RESEND_COOLDOWN_TIME = 60;
  const OTP_LENGTH = 6;

  // cooldown timer for edit verification OTP
  useEffect(() => {
    let t;
    if (editVerifyState.cooldown > 0) {
      t = setInterval(() => setEditVerifyState(prev => ({ ...prev, cooldown: prev.cooldown - 1 })), 1000);
    }
    return () => clearInterval(t);
  }, [editVerifyState.cooldown]);

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        middleName: user.middleName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        doorNumber: user.doorNumber || '',
        streetName: user.streetName || '',
        zipCode: user.zipCode || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || '',
      });
    }
  }, [user]);

  // +++ reCAPTCHA init for Edit Profile modal (phone flow) +++
  useEffect(() => {
    // Initialize and WAIT for the invisible reCAPTCHA to render, then trigger OTP send
    const initRecaptchaForEdit = async () => {
      try {
        if (showEditVerifyModal && !window.recaptchaVerifierEdit) {
          // Ensure container exists in DOM
          let container = document.getElementById('recaptcha-container-edit-profile');
          if (!container) {
            const modalBody = document.querySelector('.modal-body');
            if (modalBody) {
              container = document.createElement('div');
              container.id = 'recaptcha-container-edit-profile';
              container.style.display = 'none';
              modalBody.appendChild(container);
            }
          }

          if (!window.recaptchaVerifierEdit && container) {
            window.recaptchaVerifierEdit = new RecaptchaVerifier(auth, 'recaptcha-container-edit-profile', {
              size: 'invisible',
              callback: () => { console.log('Edit Profile reCAPTCHA verified'); },
              'expired-callback': () => { setEditVerifyError('Verification expired. Please retry.'); }
            });
            // WAIT for render to complete before attempting to send OTP
            await window.recaptchaVerifierEdit.render();

            // Only trigger send when we're at the initial send step
            if (editVerifyStep === 1) {
              // handleSendEditVerifyOtp will manage loading / cooldown / errors
              handleSendEditVerifyOtp();
            }
          } else if (showEditVerifyModal && window.recaptchaVerifierEdit && editVerifyStep === 1) {
            // Already initialized previously — ensure OTP send still triggered for this opening
            handleSendEditVerifyOtp();
          }
        }
      } catch (e) {
        console.error('Edit Profile reCAPTCHA setup error:', e);
        setEditVerifyError('Could not initialize verification. Please refresh.');
      }
    };
    initRecaptchaForEdit();
    // Note: we intentionally omit handleSendEditVerifyOtp from deps to avoid retrigger loops
  }, [showEditVerifyModal, editVerifyStep]);

  // +++ reCAPTCHA init for Change Password modal (phone flow) +++
  useEffect(() => {
    const initRecaptchaForPasswordChange = async () => {
      try {
        if (showChangePasswordModal && !window.recaptchaVerifier) {
          let container = document.getElementById('recaptcha-container-profile');
          if (!container) {
            // the container is rendered inside the modal JSX; ensure it's present
            const modalBody = document.querySelector('.modal-body');
            if (modalBody) {
              container = document.createElement('div');
              container.id = 'recaptcha-container-profile';
              container.style.display = 'none';
              modalBody.appendChild(container);
            }
          }

          if (!window.recaptchaVerifier && container) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container-profile', {
              size: 'invisible',
              callback: () => { console.log('Change Password reCAPTCHA verified'); },
              'expired-callback': () => { setPasswordChangeError('Verification expired. Please retry.'); }
            });
            // WAIT for render to complete before attempting to send OTP
            await window.recaptchaVerifier.render();

            if (passwordChangeStep === 1) {
              // Call send OTP after recaptcha is ready
              handleSendOtpForPasswordChange();
            }
          } else if (showChangePasswordModal && window.recaptchaVerifier && passwordChangeStep === 1) {
            // If already initialized, ensure OTP send occurs
            handleSendOtpForPasswordChange();
          }
        }
      } catch (e) {
        console.error('Change Password reCAPTCHA setup error:', e);
        setPasswordChangeError('Could not initialize security verification. Please refresh.');
      }
    };
    initRecaptchaForPasswordChange();
    // Note: omit handleSendOtpForPasswordChange from deps to prevent effect loops
  }, [showChangePasswordModal, passwordChangeStep]);

  // Helper: reset change-password modal state
  const resetPasswordChangeState = () => {
    // reuse existing states
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordChangeOtp('');
    setPasswordChangeStep(1);
    setPasswordChangeError('');
    setPasswordChangeSuccess('');
    setFirebaseConfirmation(null);
    // clear temporary token captured during phone OTP verification
    setTempFirebaseToken(null);
    setPhoneOtpState({ count: 0, loading: false, cooldown: 0, sent: false });
    /*
    // email resets commented out
    setEmailOtpState({ count: 0, loading: false, cooldown: 0, sent: false });
    */
    if (window.grecaptcha && window.recaptchaWidgetIdProfile) {
      try { window.grecaptcha.reset(window.recaptchaWidgetIdProfile); } catch (e) { }
    }
  };

  // --- NEW: Handle Edit button click: open modal and auto-send OTP ---
  const handleEditClick = () => {
    // Reset modal state
    setEditVerifyError('');
    setEditVerifyOtp('');
    setEditVerifyStep(1);
    setEditVerifyState({ loading: false, cooldown: 0, sent: false, count: 0 });
    // show modal — the useEffect will initialize recaptcha and trigger OTP send after render
    setShowEditVerifyModal(true);
  };

  // Open modal; reCAPTCHA effect will wait for render and then send OTP
  const handleChangePasswordClick = () => {
    resetPasswordChangeState();
    setPasswordChangeStep(1); // set initial step so the recaptcha effect knows to send
    setPasswordChangeMethod('phone');
    setShowChangePasswordModal(true);
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    resetPasswordChangeState();
  };

  // --- NEW: Send OTP specifically for Edit Profile verification (phone via Firebase) ---
  const handleSendEditVerifyOtp = async () => {
    if (editVerifyState.cooldown > 0 || editVerifyState.count >= MAX_RESEND_ATTEMPTS) return;
    setEditVerifyError('');
    setEditVerifyState(prev => ({ ...prev, loading: true, count: prev.count + 1 }));
    try {
      const appVerifier = window.recaptchaVerifierEdit;
      if (!appVerifier) throw new Error('Security verification failed to initialize. Please refresh.');
      const phoneNumber = user.phone?.startsWith('+') ? user.phone : `+91${user.phone}`;
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setFirebaseConfirmation(confirmation);
      setEditVerifyState(prev => ({ ...prev, sent: true, cooldown: RESEND_COOLDOWN_TIME }));
      setEditVerifyStep(2);
    } catch (err) {
      console.error('Send Edit OTP error:', err);
      setEditVerifyError(getFriendlyErrorMessage(err));
      if (window.grecaptcha && window.recaptchaWidgetIdProfile) {
        try { window.grecaptcha.reset(window.recaptchaWidgetIdProfile); } catch (e) { }
      }
    } finally {
      setEditVerifyState(prev => ({ ...prev, loading: false }));
    }
  };

  // --- NEW: Verify OTP for Edit Profile, call backend to confirm Firebase token, then enable editing ---
  const handleVerifyEditOtp = async () => {
    setEditVerifyError('');
    if (editVerifyOtp.length !== OTP_LENGTH) {
      setEditVerifyError(`Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }
    setEditVerifyState(prev => ({ ...prev, loading: true }));
    try {
      if (!firebaseConfirmation) throw new Error('Verification session has expired. Please resend the OTP.');
      const result = await firebaseConfirmation.confirm(editVerifyOtp);
      const firebaseToken = await result.user.getIdToken();
      // Call backend to verify token and ensure phone matches logged-in user
      await api.post('/auth/verify-edit-firebase', { firebaseToken });
      // Success: close modal and enable edit mode
      setShowEditVerifyModal(false);
      setEditMode(true);
    } catch (err) {
      console.error('Verify Edit OTP error:', err);
      setEditVerifyError(getFriendlyErrorMessage(err));
    } finally {
      setEditVerifyState(prev => ({ ...prev, loading: false }));
    }
  };

  const validatePassword = (pw) => {
    if (pw.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(pw)) return 'Password must include an uppercase letter.';
    if (!/[a-z]/.test(pw)) return 'Password must include a lowercase letter.';
    if (!/[0-9]/.test(pw)) return 'Password must include a number.';
    if (!/[!@#$%^&*]/.test(pw)) return 'Password must include a special character (!@#$%^&*).';
    return null;
  };

  // +++ FRIENDLY ERROR MAPPING (do NOT expose raw errors to users) +++
  const getFriendlyErrorMessage = (err) => {
    // Log full error for developers
    console.error('Password change flow error:', err);

    if (!err) return 'Something went wrong. Please try again.';
    const code = err?.code || err?.response?.data?.code || '';
    const rawMsg = (err?.message || err?.response?.data?.error || '').toString();

    // Firebase / SMS common cases
    if (rawMsg.includes('quota') || code === 'auth/quota-exceeded') {
      return 'SMS service is temporarily limited. Try email verification or try again later.';
    }
    if (rawMsg.toLowerCase().includes('invalid') && rawMsg.toLowerCase().includes('code')) {
      return 'Invalid or expired OTP. Please request a new one.';
    }
    if (rawMsg.toLowerCase().includes('too-many-requests') || code === 'auth/too-many-requests') {
      return 'Too many attempts. Please wait a few minutes and try again.';
    }
    if (rawMsg.toLowerCase().includes('expired') || rawMsg.toLowerCase().includes('expired-callback')) {
      return 'Verification expired. Please resend the OTP and try again.';
    }

    // Network / server issues
    if (rawMsg.toLowerCase().includes('network') || rawMsg.toLowerCase().includes('failed to fetch') || rawMsg.toLowerCase().includes('timeout')) {
      return 'Network problem detected. Check your connection and try again.';
    }
    if (err?.response?.status >= 500) {
      return 'Server is currently unavailable. Please try again later.';
    }

    // Backend-provided friendly message (prefer it)
    if (err?.response?.data?.error) return err.response.data.error;

    // Fallback generic message
    return 'Could not complete the operation. Please refresh and try again';
  };

  // Step navigation
  const handleChooseMethod = (method) => {
    setPasswordChangeMethod(method);
    setPasswordChangeStep(2);
  };

  // Send OTP (phone uses Firebase)
  const handleSendOtpForPasswordChange = async () => {
    setPasswordChangeError('');
    setPasswordChangeSuccess('');
    if (passwordChangeMethod === 'email') {
      /* Email flow commented out - kept for reference
      if (emailOtpState.cooldown > 0 || emailOtpState.count >= MAX_RESEND_ATTEMPTS) return;
      setEmailOtpState(prev => ({ ...prev, loading: true, count: prev.count + 1 }));
      try {
        // Call the new protected endpoint that sends OTP to the logged-in user's email
        await api.post('/auth/send-change-password-email-otp');
        setEmailOtpState(prev => ({ ...prev, sent: true, cooldown: RESEND_COOLDOWN_TIME }));
        setPasswordChangeStep(3);
      } catch (err) {
        setPasswordChangeError(getFriendlyErrorMessage(err));
      } finally {
        setEmailOtpState(prev => ({ ...prev, loading: false }));
      }
      */
      return; // prevent falling through to phone branch when email chosen (email disabled)
    } else {
      if (phoneOtpState.cooldown > 0 || phoneOtpState.count >= MAX_RESEND_ATTEMPTS) return;
      setPhoneOtpState(prev => ({ ...prev, loading: true, count: prev.count + 1 }));
      try {
        const appVerifier = window.recaptchaVerifier;
        if (!appVerifier) throw new Error('Security verification not initialized. Refresh the page.');
        const phoneNumber = user.phone?.startsWith('+') ? user.phone : `+91${user.phone}`;
        const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
        setFirebaseConfirmation(confirmation);
        setPhoneOtpState(prev => ({ ...prev, sent: true, cooldown: RESEND_COOLDOWN_TIME }));
        setPasswordChangeStep(3);
      } catch (err) {
        // log technical error and show friendly message
        console.error('Send OTP (profile) error:', err);
        setPasswordChangeError(getFriendlyErrorMessage(err));
        if (window.grecaptcha && window.recaptchaWidgetIdProfile) {
          try { window.grecaptcha.reset(window.recaptchaWidgetIdProfile); } catch (e) { }
        }
      } finally {
        setPhoneOtpState(prev => ({ ...prev, loading: false }));
      }
    }
  };

  // Verify OTP (modal) - for email, request token and store for reset-password call
  const handleVerifyOtpForPasswordChange = async () => {
    setPasswordChangeError('');
    if (passwordChangeOtp.length !== OTP_LENGTH) {
      setPasswordChangeError(`Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }
    if (passwordChangeMethod === 'email') {
      /* Email verification flow commented out - kept for reference
      setEmailOtpState(prev => ({ ...prev, loading: true }));
      try {
        // Verify email OTP and obtain a temporary reset token from backend
        const res = await api.post('/auth/verify-otp', { email: user.email, otp: passwordChangeOtp, purpose: 'reset-password' });
        if (res.data && res.data.token) {
          sessionStorage.setItem('temp_reset_token', res.data.token);
          setPasswordChangeStep(4);
        } else {
          throw new Error(res.data?.error || 'Email OTP verification failed.');
        }
      } catch (err) {
        setPasswordChangeError(getFriendlyErrorMessage(err));
      } finally {
        setEmailOtpState(prev => ({ ...prev, loading: false }));
      }
      */
      return; // prevent falling through to phone branch when email chosen (email disabled)
    } else {
      setPhoneOtpState(prev => ({ ...prev, loading: true }));
    }

    try {
      if (passwordChangeMethod === 'email') {
        // (commented out above)
      } else {
        if (!firebaseConfirmation) throw new Error('No Firebase confirmation available. Re-send OTP.');
        // Confirm OTP and extract a Firebase ID token immediately, store for final step.
        const result = await firebaseConfirmation.confirm(passwordChangeOtp);
        const token = await result.user.getIdToken();
        setTempFirebaseToken(token);
        // Optionally keep firebaseConfirmation if you need it elsewhere
        setPasswordChangeStep(4);
      }
    } catch (err) {
      setPasswordChangeError(getFriendlyErrorMessage(err));
    } finally {
      /*
      setEmailOtpState(prev => ({ ...prev, loading: false })); // email flow commented
      */
      setPhoneOtpState(prev => ({ ...prev, loading: false }));
    }
  };

  // Final: set new password (modal)
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSuccess('');

    const pwdErr = validatePassword(newPassword);
    if (pwdErr) { setPasswordChangeError(pwdErr); return; }
    if (newPassword !== confirmNewPassword) { setPasswordChangeError('New passwords do not match.'); return; }

    setLoadingFinal(true)
    try {
      if (passwordChangeMethod === 'email') {
        /* Email final step commented out - kept for reference
        // Use reset-password endpoint with the token we stored after email OTP verification
        const resetToken = sessionStorage.getItem('temp_reset_token');
        if (!resetToken) throw new Error('Session expired. Please start over.');
        await api.post('/auth/reset-password', { token: resetToken, newPassword });
        sessionStorage.removeItem('temp_reset_token');
        setPasswordChangeSuccess('Password changed successfully via email OTP!');
        */
        throw new Error('Email-based password reset is currently disabled.');
      } else {
        // Use the token captured at OTP verification time
        if (!tempFirebaseToken) throw new Error('Verification session expired. Please start over.');
        await api.post('/auth/change-password-firebase', { firebaseToken: tempFirebaseToken, newPassword });
        setPasswordChangeSuccess('Password changed successfully via phone OTP!');
      }
      setTimeout(handleCloseChangePasswordModal, 1200);
    } catch (err) {
      setPasswordChangeError(getFriendlyErrorMessage(err));
    } finally {
      setLoadingFinal(false);
    }
  };

  if (!user || !user.email) {
    return (
      <div className="profile-empty">
        <h2>Profile</h2>
        <p>No user information found. Please sign in.</p>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  const handleSave = async (e) => {
    e.preventDefault();
    let errors = {};
    if (!form.firstName) errors.firstName = 'First name is required.';
    if (!form.lastName) errors.lastName = 'Last name is required.';
    if (!form.email) errors.email = 'Email is required.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fill all required fields.');
      return;
    }
    const result = await updateProfile(form);
    if (result.success) {
      setSuccess('Profile updated successfully!');
      setEditMode(false);
      setFieldErrors({});
    } else {
      let backendFieldErrors = {};
      if (result.error && typeof result.error === 'object' && result.error.errors) {
        for (const key in result.error.errors) {
          backendFieldErrors[key] = result.error.errors[key].message;
        }
        setFieldErrors(backendFieldErrors);
        setError('Please correct the highlighted fields.');
      } else if (
        result.error &&
        result.error.toLowerCase().includes('lastname') &&
        result.error.toLowerCase().includes('required')
      ) {
        setFieldErrors({ lastName: 'Last name is required.' });
        setError('Please enter your last name.');
      } else {
        setError(result.error || 'Failed to update profile.');
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setError('All password fields are required.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setPwLoading(true);
    const result = await changePassword({ oldPassword, newPassword });
    setPwLoading(false);
    if (result.success) {
      setSuccess('Password changed successfully!');
      // inline flow: clear OTP / password states
      setOtpVerified(false);
      setShowOtpField(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      logout();
      navigate('/login');
    } else {
      setError(result.error || 'Failed to change password.');
    }
  };

  // JSX: remove the old inline section — replaced with modal-only flow below
  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2 className="profile-name">{form.firstName} {form.middleName} {form.lastName}</h2>
        <div className="profile-email">{form.email}</div>
      </div>

      <div className="divider" />

      {success && <div className="alert-success">{success}</div>}
      {error && <div className="alert-error">{error}</div>}

      {/* +++ EDIT PROFILE VERIFICATION MODAL +++ */}
      {showEditVerifyModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <button type="button" className="modal-close-btn" onClick={() => setShowEditVerifyModal(false)}>&times;</button>
            <h4>Verify Your Identity</h4>
            <p className="text-center text-muted small mb-3">For your security, please complete a quick verification to edit your profile.</p>

            {editVerifyError && (<div className="alert alert-danger">{editVerifyError}</div>)}

            {editVerifyState.loading && !editVerifyState.sent && (
              <div className="text-center p-4">
                <FaSpinner className="fa-spin fa-2x text-primary" />
                <p className="mt-3 text-muted">Sending verification code...</p>
              </div>
            )}

            {editVerifyStep === 2 && (
              <>
                <p className="text-center mb-3">Enter the OTP sent to your mobile ({user.phone}).</p>
                <div className="mb-3">
                  <input type="text" className="form-control text-center" placeholder={`Enter ${OTP_LENGTH}-digit OTP`} value={editVerifyOtp} onChange={(e) => setEditVerifyOtp(e.target.value)} maxLength={OTP_LENGTH} />
                </div>
                <button className="btn btn-primary w-100 mb-3" onClick={handleVerifyEditOtp} disabled={editVerifyOtp.length !== OTP_LENGTH || editVerifyState.loading}>
                  {editVerifyState.loading ? <FaSpinner className="fa-spin" /> : 'Verify & Edit'}
                </button>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  {editVerifyState.cooldown > 0 ? (
                    <small className="text-muted">Resend in {editVerifyState.cooldown}s</small>
                  ) : (
                    <button className="btn btn-link p-0" onClick={handleSendEditVerifyOtp} disabled={editVerifyState.loading || editVerifyState.count >= MAX_RESEND_ATTEMPTS}>
                      {editVerifyState.loading ? <FaSpinner className="fa-spin" /> : 'Resend OTP'}
                    </button>
                  )}
                </div>
              </>
            )}

            <div id="recaptcha-container-edit-profile" style={{ display: 'none' }}></div>
          </div>
        </div>
      )}

      {/* +++ CHANGE PASSWORD MODAL (full-screen overlay style) +++ */}
      {showChangePasswordModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <button type="button" className="modal-close-btn" onClick={handleCloseChangePasswordModal}>&times;</button>
            <h4>Change Password</h4>
            {passwordChangeError && (<div className="alert alert-danger">{passwordChangeError}</div>)}
            {passwordChangeSuccess && (<div className="alert alert-success">{passwordChangeSuccess}</div>)}

            {/* Show a loading spinner while the initial OTP is being sent */}
            {phoneOtpState.loading && !phoneOtpState.sent && (
              <div className="text-center p-4">
                <FaSpinner className="fa-spin fa-2x text-primary" />
                <p className="mt-3 text-muted">Sending verification code...</p>
              </div>
            )}

            {/* Step 3: Verify OTP (Now the first visible step) */}
            {passwordChangeStep === 3 && (
              <>
                <p className="text-center mb-3">Enter the OTP sent to your mobile ({user.phone}).</p>
                <div className="mb-3">
                  <input type="text" className="form-control text-center" placeholder={`Enter ${OTP_LENGTH}-digit OTP`} value={passwordChangeOtp} onChange={(e) => setPasswordChangeOtp(e.target.value)} maxLength={OTP_LENGTH} />
                </div>
                <button className="btn btn-primary w-100 mb-3" onClick={handleVerifyOtpForPasswordChange} disabled={passwordChangeOtp.length !== OTP_LENGTH || phoneOtpState.loading}>
                  {phoneOtpState.loading ? <FaSpinner className="fa-spin" /> : 'Verify OTP'}
                </button>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  {phoneOtpState.cooldown > 0 ? (
                    <small className="text-muted">Resend in {phoneOtpState.cooldown}s</small>
                  ) : (
                    <button className="btn btn-link p-0" onClick={handleSendOtpForPasswordChange} disabled={phoneOtpState.loading || phoneOtpState.count >= MAX_RESEND_ATTEMPTS}>
                      {phoneOtpState.loading ? <FaSpinner className="fa-spin" /> : 'Resend OTP'}
                    </button>
                  )}
                  {phoneOtpState.count >= MAX_RESEND_ATTEMPTS && (
                    <small className="text-danger">Max resend attempts reached.</small>
                  )}
                </div>
              </>
            )}

            {/* Step 4: Set New Password */}
            {passwordChangeStep === 4 && (
              <form onSubmit={handleSetNewPassword}>
                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label">New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: '#6c757d'
                      }}
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="confirmNewPassword" className="form-label">Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmNewPassword"
                      className="form-control"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: '#6c757d'
                      }}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary w-100" disabled={loadingFinal}>
                  {loadingFinal ? <FaSpinner className="fa-spin" /> : 'Set New Password'}
                </button>
              </form>
            )}

            <div id="recaptcha-container-profile" style={{ display: 'none' }}></div>
          </div>
        </div>
      )}

      {!editMode ? (
        <div className="details-section">
          <div className="info-row"><span className="info-label">Full Name:</span> {form.firstName} {form.middleName} {form.lastName}</div>
          <div className="info-row"><span className="info-label">Email:</span> {form.email}</div>
          {form.phone && <div className="info-row"><span className="info-label">Phone:</span> {form.phone}</div>}
          {(form.doorNumber || form.streetName || form.city || form.state || form.country || form.zipCode) && (
            <div className="info-row">
              <span className="info-label">Address:</span>
              {[form.doorNumber, form.streetName, form.city, form.state, form.country, form.zipCode].filter(Boolean).join(', ')}
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            <button className="btn-edit" onClick={handleEditClick}>Edit Profile</button>
            <button className="btn-edit" onClick={handleChangePasswordClick} style={{ background: '#27ae60' }}>Change Password</button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="edit-form">
          <div className="form-group">
            <label className="form-label">First Name:</label>
            <input
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              className={`input ${fieldErrors.firstName ? 'input-error' : ''}`}
              required
            />
            {fieldErrors.firstName && <div className="field-error">{fieldErrors.firstName}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Middle Name:</label>
            <input type="text" name="middleName" value={form.middleName} onChange={handleChange} className="input" />
          </div>

          <div className="form-group">
            <label className="form-label">Last Name:</label>
            <input
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              className={`input ${fieldErrors.lastName ? 'input-error' : ''}`}
              required
            />
            {fieldErrors.lastName && <div className="field-error">{fieldErrors.lastName}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Email:</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={`input ${fieldErrors.email ? 'input-error' : ''}`}
              required
            />
            {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Phone:</label>
            <input type="tel" name="phone" value={form.phone} onChange={handleChange} className="input" />
          </div>

          <div className="form-group">
            <label className="form-label">Door Number:</label>
            <input type="text" name="doorNumber" value={form.doorNumber} onChange={handleChange} className="input" />
          </div>

          <div className="form-group">
            <label className="form-label">Street Name:</label>
            <input type="text" name="streetName" value={form.streetName} onChange={handleChange} className="input" />
          </div>

          <div className="form-row">
            <div className="form-group small">
              <label className="form-label">City:</label>
              <input type="text" name="city" value={form.city} onChange={handleChange} className="input" />
            </div>
            <div className="form-group small">
              <label className="form-label">State:</label>
              <input type="text" name="state" value={form.state} onChange={handleChange} className="input" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group small">
              <label className="form-label">Country:</label>
              <input type="text" name="country" value={form.country} onChange={handleChange} className="input" />
            </div>
            <div className="form-group small">
              <label className="form-label">Zip Code:</label>
              <input type="text" name="zipCode" value={form.zipCode} onChange={handleChange} className="input" />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-success">Save</button>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setEditMode(false);
                setForm({
                  firstName: user.firstName || '',
                  middleName: user.middleName || '',
                  lastName: user.lastName || '',
                  email: user.email || '',
                  phone: user.phone || '',
                  doorNumber: user.doorNumber || '',
                  streetName: user.streetName || '',
                  zipCode: user.zipCode || '',
                  city: user.city || '',
                  state: user.state || '',
                  country: user.country || '',
                });
                setError('');
                setSuccess('');
                setFieldErrors({});
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* +++ CHANGE PASSWORD MODAL +++ */}
      {showChangePasswordModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Password</h5>
                <button type="button" className="btn-close" onClick={handleCloseChangePasswordModal}></button>
              </div>
              <div className="modal-body">
                {passwordChangeError && (<div className="alert alert-danger">{passwordChangeError}</div>)}
                {passwordChangeSuccess && (<div className="alert alert-success">{passwordChangeSuccess}</div>)}

                {/* Show a loading spinner while the initial OTP is being sent */}
                {phoneOtpState.loading && !phoneOtpState.sent && (
                  <div className="text-center p-4">
                    <FaSpinner className="fa-spin fa-2x text-primary" />
                    <p className="mt-3 text-muted">Sending verification code...</p>
                  </div>
                )}

                {/* Step 3: Verify OTP (Now the first visible step) */}
                {passwordChangeStep === 3 && (
                  <>
                    <p className="text-center mb-3">Enter the OTP sent to your mobile ({user.phone}).</p>
                    <div className="mb-3">
                      <input type="text" className="form-control text-center" placeholder={`Enter ${OTP_LENGTH}-digit OTP`} value={passwordChangeOtp} onChange={(e) => setPasswordChangeOtp(e.target.value)} maxLength={OTP_LENGTH} />
                    </div>
                    <button className="btn btn-primary w-100 mb-3" onClick={handleVerifyOtpForPasswordChange} disabled={passwordChangeOtp.length !== OTP_LENGTH || phoneOtpState.loading}>
                      {phoneOtpState.loading ? <FaSpinner className="fa-spin" /> : 'Verify OTP'}
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      {phoneOtpState.cooldown > 0 ? (
                        <small className="text-muted">Resend in {phoneOtpState.cooldown}s</small>
                      ) : (
                        <button className="btn btn-link p-0" onClick={handleSendOtpForPasswordChange} disabled={phoneOtpState.loading || phoneOtpState.count >= MAX_RESEND_ATTEMPTS}>
                          {phoneOtpState.loading ? <FaSpinner className="fa-spin" /> : 'Resend OTP'}
                        </button>
                      )}
                      {phoneOtpState.count >= MAX_RESEND_ATTEMPTS && (
                        <small className="text-danger">Max resend attempts reached.</small>
                      )}
                    </div>
                  </>
                )}

                {/* Step 4: Set New Password */}
                {passwordChangeStep === 4 && (
                  <form onSubmit={handleSetNewPassword}>
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">New Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          id="newPassword"
                          className="form-control"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          style={{ paddingRight: '40px' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            color: '#6c757d'
                          }}
                        >
                          {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="confirmNewPassword" className="form-label">Confirm New Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmNewPassword"
                          className="form-control"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          style={{ paddingRight: '40px' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            color: '#6c757d'
                          }}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary w-100" disabled={loadingFinal}>
                      {loadingFinal ? <FaSpinner className="fa-spin" /> : 'Set New Password'}
                    </button>
                  </form>
                )}

                <div id="recaptcha-container-profile" style={{ display: 'none' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Profile;